import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Button, Alert, Accordion, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import {
    SMS_PROCESS_PRESETS,
    SMS_LANGUAGES,
    SMS_MESSAGE_PROCESS_KEYS,
    SMS_MESSAGE_SECTION_LABELS,
    SMS_MAX_CHARS,
    SMS_SEGMENT_CHARS,
    pruneSmsMessagesForProcesses,
    validateSmsForm,
    buildSmsManagementPayload,
    mergeTemplateIntoMessages,
    normalizeProcessesFromApi,
    normalizeSmsMessagesFromApi,
    getSmsProcessOptionId,
    getProcessesFromPresetId,
    deriveExtraLanguagesFromMessages,
    getLanguageCodesForProcess,
    getSmsLanguageLabel,
    isValidSmsLanguageCode,
    normalizeSmsLanguageCode,
} from '../../constants/smsManagement';
import './SmsManagementForm.css';

const serializeProcesses = (processes) => JSON.stringify(normalizeProcessesFromApi(processes));
const serializeMessages = (messages) => JSON.stringify(normalizeSmsMessagesFromApi(messages));

const SmsManagementForm = ({
    partnerField = null,
    initialProcesses = ['no_sms'],
    initialMessages = null,
    onSubmit,
    onCancel,
    loading = false,
    submitLabel = 'Enregistrer',
    isUpdate = false,
    externalError = null,
}) => {
    const [processPresetId, setProcessPresetId] = useState(
        () => getSmsProcessOptionId(normalizeProcessesFromApi(initialProcesses)) || 'no_sms'
    );
    const [smsMessages, setSmsMessages] = useState(() => normalizeSmsMessagesFromApi(initialMessages));
    const [extraLanguagesByProcess, setExtraLanguagesByProcess] = useState(() => (
        deriveExtraLanguagesFromMessages(normalizeSmsMessagesFromApi(initialMessages))
    ));
    const [newLanguageByProcess, setNewLanguageByProcess] = useState({});
    const [languageErrorsByProcess, setLanguageErrorsByProcess] = useState({});
    const [formErrors, setFormErrors] = useState([]);

    const initialProcessesKey = serializeProcesses(initialProcesses);
    const initialMessagesKey = serializeMessages(initialMessages);
    const lastSyncedInitialKey = useRef('');

    useEffect(() => {
        const syncKey = `${initialProcessesKey}|${initialMessagesKey}`;
        if (lastSyncedInitialKey.current === syncKey) {
            return;
        }
        lastSyncedInitialKey.current = syncKey;
        const presetId = getSmsProcessOptionId(normalizeProcessesFromApi(initialProcesses)) || 'no_sms';
        setProcessPresetId(presetId);
        const normalizedMessages = normalizeSmsMessagesFromApi(initialMessages);
        setSmsMessages(normalizedMessages);
        setExtraLanguagesByProcess(deriveExtraLanguagesFromMessages(normalizedMessages));
        setNewLanguageByProcess({});
        setLanguageErrorsByProcess({});
    }, [initialProcessesKey, initialMessagesKey, initialProcesses, initialMessages]);

    const smsProcesses = useMemo(
        () => getProcessesFromPresetId(processPresetId),
        [processPresetId]
    );

    const showMessagesSection = processPresetId !== 'no_sms';
    const activeMessageProcesses = SMS_MESSAGE_PROCESS_KEYS.filter((key) => smsProcesses.includes(key));

    const handlePresetChange = (presetId) => {
        const nextProcesses = getProcessesFromPresetId(presetId);
        setProcessPresetId(presetId);
        setSmsMessages((prev) => pruneSmsMessagesForProcesses(prev, nextProcesses));
        setFormErrors([]);
    };

    const handleMessageChange = (processKey, langCode, value) => {
        setSmsMessages((prev) => ({
            ...prev,
            [processKey]: {
                ...(prev[processKey] || {}),
                [langCode]: value,
            },
        }));
    };

    const isDefaultLanguage = (code) => SMS_LANGUAGES.some((lang) => lang.code === code);

    const handleAddLanguage = (processKey) => {
        const raw = (newLanguageByProcess[processKey] || '').trim();
        const code = normalizeSmsLanguageCode(raw);
        const existing = getLanguageCodesForProcess(smsMessages, processKey, extraLanguagesByProcess[processKey] || []);

        if (!code) {
            setLanguageErrorsByProcess((prev) => ({
                ...prev,
                [processKey]: 'Saisissez un code langue (ex. it-it).',
            }));
            return;
        }
        if (!isValidSmsLanguageCode(code)) {
            setLanguageErrorsByProcess((prev) => ({
                ...prev,
                [processKey]: 'Format invalide. Utilisez xx-xx (ex. it-it, pt-br, es-es).',
            }));
            return;
        }
        if (existing.includes(code)) {
            setLanguageErrorsByProcess((prev) => ({
                ...prev,
                [processKey]: `La langue ${code} existe déjà pour ce processus.`,
            }));
            return;
        }

        setExtraLanguagesByProcess((prev) => ({
            ...prev,
            [processKey]: [...(prev[processKey] || []), code],
        }));
        setNewLanguageByProcess((prev) => ({ ...prev, [processKey]: '' }));
        setLanguageErrorsByProcess((prev) => ({ ...prev, [processKey]: null }));
    };

    const handleRemoveLanguage = (processKey, langCode) => {
        if (isDefaultLanguage(langCode)) return;

        setExtraLanguagesByProcess((prev) => ({
            ...prev,
            [processKey]: (prev[processKey] || []).filter((code) => code !== langCode),
        }));
        setSmsMessages((prev) => {
            const block = { ...(prev[processKey] || {}) };
            delete block[langCode];
            return { ...prev, [processKey]: block };
        });
    };

    const renderLanguageField = (processKey, code) => {
        const value = smsMessages[processKey]?.[code] || '';
        const label = getSmsLanguageLabel(code);
        const isCustom = !isDefaultLanguage(code);

        return (
            <Form.Group className="mb-3" key={`${processKey}-${code}`}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="mb-0">
                        {label}
                        <span className="text-muted ms-1 sms-lang-code">({code})</span>
                        {isCustom && (
                            <Badge bg="info" className="ms-2" style={{ fontSize: '0.65rem' }}>
                                Personnalisée
                            </Badge>
                        )}
                    </Form.Label>
                    <div className="d-flex align-items-center gap-2">
                        <Badge
                            bg={value.length > SMS_MAX_CHARS ? 'danger' : 'light'}
                            text={value.length > SMS_MAX_CHARS ? 'white' : 'muted'}
                            className="sms-char-badge"
                        >
                            {getCharCountLabel(value)}
                        </Badge>
                        {isCustom && (
                            <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                className="py-0 px-1"
                                onClick={() => handleRemoveLanguage(processKey, code)}
                                disabled={loading}
                                title="Supprimer cette langue"
                            >
                                <FiTrash2 size={14} />
                            </Button>
                        )}
                    </div>
                </div>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={value}
                    onChange={(e) => handleMessageChange(processKey, code, e.target.value)}
                    disabled={loading}
                    placeholder={`Message ${label} (${code})…`}
                    maxLength={SMS_MAX_CHARS + 50}
                    isInvalid={value.length > SMS_MAX_CHARS}
                />
            </Form.Group>
        );
    };

    const handlePrefill = (brand) => {
        setSmsMessages((prev) => mergeTemplateIntoMessages(prev, brand, smsProcesses));
        setFormErrors([]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const prunedMessages = pruneSmsMessagesForProcesses(smsMessages, smsProcesses);
        const errors = validateSmsForm(smsProcesses, prunedMessages);
        if (errors.length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormErrors([]);
        const payload = buildSmsManagementPayload(smsProcesses, prunedMessages, { isUpdate });
        onSubmit(payload);
    };

    const getCharCountLabel = (text) => {
        const len = (text || '').length;
        const segments = Math.max(1, Math.ceil(len / SMS_SEGMENT_CHARS) || 1);
        return `${len} car. (~${segments} segment${segments > 1 ? 's' : ''})`;
    };

    return (
        <Form onSubmit={handleSubmit}>
            {(formErrors.length > 0 || externalError) && (
                <Alert variant="danger" className="mb-3">
                    {externalError && <div>{externalError}</div>}
                    {formErrors.map((err, index) => (
                        <div key={`${err}-${index}`}>{err}</div>
                    ))}
                </Alert>
            )}

            {partnerField}

            <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">Processus SMS *</Form.Label>
                <Form.Select
                    value={processPresetId}
                    onChange={(e) => handlePresetChange(e.target.value)}
                    disabled={loading}
                    required
                >
                    {SMS_PROCESS_PRESETS.map(({ id, label }) => (
                        <option key={id} value={id}>
                            {label}
                        </option>
                    ))}
                </Form.Select>
                <Form.Text className="text-muted d-block mt-2">
                    Déclenchement : bulk SMS via check-claims. Relance RIB : rappels coordonnées bancaires.
                </Form.Text>
            </Form.Group>

            {showMessagesSection && activeMessageProcesses.length > 0 && (
                <div className="sms-messages-section mb-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <Form.Label className="fw-semibold mb-0">Messages SMS</Form.Label>
                        <div className="d-flex gap-2">
                            <Button
                                type="button"
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handlePrefill('flower')}
                                disabled={loading}
                            >
                                Préremplir Flower
                            </Button>
                            <Button
                                type="button"
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handlePrefill('odalys')}
                                disabled={loading}
                            >
                                Préremplir Odalys
                            </Button>
                        </div>
                    </div>
                    <Form.Text className="text-muted d-block mb-3">
                        Langues par défaut : fr-fr, en-us, de-de, nl-nl. Vous pouvez ajouter d&apos;autres langues
                        (format xx-xx, ex. it-it, pt-br). Max {SMS_MAX_CHARS} caractères par message.
                    </Form.Text>

                    <Accordion key={processPresetId} defaultActiveKey={activeMessageProcesses} alwaysOpen>
                        {activeMessageProcesses.map((processKey) => {
                            const languageCodes = getLanguageCodesForProcess(
                                smsMessages,
                                processKey,
                                extraLanguagesByProcess[processKey] || []
                            );

                            return (
                            <Accordion.Item eventKey={processKey} key={processKey}>
                                <Accordion.Header>
                                    {SMS_MESSAGE_SECTION_LABELS[processKey]}
                                </Accordion.Header>
                                <Accordion.Body>
                                    {languageCodes.map((code) => renderLanguageField(processKey, code))}

                                    <div className="sms-add-language-row border-top pt-3 mt-2">
                                        <Form.Label className="small fw-semibold mb-2">
                                            Ajouter une langue
                                        </Form.Label>
                                        <InputGroup size="sm" className="mb-1">
                                            <Form.Control
                                                type="text"
                                                placeholder="it-it, pt-br, es-es…"
                                                value={newLanguageByProcess[processKey] || ''}
                                                onChange={(e) => {
                                                    setNewLanguageByProcess((prev) => ({
                                                        ...prev,
                                                        [processKey]: e.target.value,
                                                    }));
                                                    if (languageErrorsByProcess[processKey]) {
                                                        setLanguageErrorsByProcess((prev) => ({
                                                            ...prev,
                                                            [processKey]: null,
                                                        }));
                                                    }
                                                }}
                                                disabled={loading}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddLanguage(processKey);
                                                    }
                                                }}
                                            />
                                            <Button
                                                type="button"
                                                variant="outline-primary"
                                                onClick={() => handleAddLanguage(processKey)}
                                                disabled={loading}
                                            >
                                                <FiPlus className="me-1" />
                                                Ajouter
                                            </Button>
                                        </InputGroup>
                                        {languageErrorsByProcess[processKey] && (
                                            <div className="text-danger small">
                                                {languageErrorsByProcess[processKey]}
                                            </div>
                                        )}
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                            );
                        })}
                    </Accordion>
                </div>
            )}

            <div className="d-flex justify-content-end gap-2">
                {onCancel && (
                    <Button variant="secondary" type="button" onClick={onCancel} disabled={loading}>
                        Annuler
                    </Button>
                )}
                <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Enregistrement…
                        </>
                    ) : (
                        submitLabel
                    )}
                </Button>
            </div>
        </Form>
    );
};

export default SmsManagementForm;
